import { Typography } from "antd";
import React from "react";

const { Text } = Typography;

export type BrandLogoProps = {
    /** Путь к png/svg/jpg. В Next клади в /public, в Vite — в /public или импортируй. */
    src: string;
    /** Alt-текст для доступности. */
    alt?: string;

    /** Фиксированная высота (по умолчанию 32). Ширина подстроится по пропорциям. */
    height?: number;
    /** Явная ширина (обычно не нужна, лучше доверить пропорциям). */
    width?: number;

    /** Скругление: true -> круглое, число -> px радиус. */
    rounded?: boolean | number;
    /** Тень под логотипом. */
    shadow?: boolean;

    /** Текст рядом/снизу (например, название бренда). */
    text?: React.ReactNode;
    /** Позиция текста относительно иконки. */
    textPosition?: "right" | "bottom";
    /** Отступ между иконкой и текстом. */
    gap?: number;

    /** Делает логотип ссылкой. */
    href?: string;
    target?: React.HTMLAttributeAnchorTarget;
    rel?: string;
    /** Если true — не оборачиваем в <a>, чтобы родитель сам обёрнул (Next/Router Link). */
    asChild?: boolean;

    /** Доп. классы/стили на внешнюю оболочку. */
    className?: string;
    style?: React.CSSProperties;

    /** Пробрасываемые пропы прямо в <img>. */
    imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
};

export function BrandLogo({
    src,
    alt = "Brand logo",
    height = 20,
    width = 30,
    rounded = false,
    shadow = false,
    text,
    textPosition = "right",
    gap = 8,
    href,
    target,
    rel,
    asChild = false,
    className,
    style,
    imgProps,
}: BrandLogoProps) {
    const borderRadius = typeof rounded === "number" ? rounded : rounded ? 9999 : 0;

    const imgEl = (
        <img
            src={src}
            alt={alt}
            height={height}
            width={width}
            draggable={false}
            style={{
                display: "block",
                height: height,
                width: width,
                objectFit: "contain",
                borderRadius,
                boxShadow: shadow ? "0 2px 8px rgba(0,0,0,0.08)" : undefined,
            }}
            {...imgProps}
        />
    );

    const content = (
        <span
            style={{
                display: "inline-flex",
                flexDirection: textPosition === "bottom" ? "column" : "row",
                alignItems: textPosition === "bottom" ? "center" : "center",
                gap: text ? gap : 0,
                lineHeight: 0, // убираем базовый зазор под картинкой
            }}
        >
            {imgEl}
            {text ? (
                <Text
                    strong
                    style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {text}
                </Text>
            ) : null}
        </span>
    );

    if (asChild) {
        // Родитель сам оборачивает, например <Link>...</Link>
        return content;
    }

    if (href) {
        return (
            <a
                href={href}
                target={target}
                rel={rel ?? (target === "_blank" ? "noopener noreferrer" : undefined)}
                aria-label={alt}
                className={className}
                style={style}
            >
                {content}
            </a>
        );
    }
    // Просто логотип без ссылки
    return (
        <span className={className} style={style} aria-label={alt}>
            {content}
        </span>
    );
}
